/**
 * Shuffle déterministe (Fisher-Yates) seedé par une chaîne ou un nombre.
 *
 * Utilisé côté print pour que le mélange des cibles (relier), banque de mots
 * (texte_trou drag) ou items (classer) soit **reproductible** : un même
 * payload produit toujours le même visuel à chaque render, y compris après
 * un F5 ou une réouverture de l'aperçu.
 *
 * Les renderers élève (VueQuestion/renderers/*) utilisent `Math.random()`
 * qui re-mélange à chaque render — inacceptable pour le print car ça casse
 * l'identité de la feuille imprimée vs l'aperçu à l'écran.
 */

// mulberry32 — PRNG seedé rapide, qualité suffisante pour un shuffle visuel.
const mulberry32 = (seed) => {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};

// FNV-1a 32-bit : hash rapide d'une string pour dériver un seed numérique.
const hashStr = (s) => {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
        h = Math.imul(h ^ s.charCodeAt(i), 16777619);
    }
    return h >>> 0;
};

export const seededShuffle = (arr, seed) => {
    if (!Array.isArray(arr) || arr.length <= 1) return [...(arr || [])];
    const seedNum = typeof seed === 'number' ? seed : hashStr(String(seed || ''));
    const rand = mulberry32(seedNum);
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

/**
 * Dérive un seed stable à partir d'un payload d'exercice.
 * Priorité : id (banque) > generatorKey + contenu court > originalIndex > 0.
 */
export const seedFromPayload = (payload, fallback = 0) => {
    if (!payload) return fallback;
    if (payload.id != null) return payload.id;
    const genKey = payload?._meta?.generatorKey;
    if (genKey) {
        // On ajoute un peu de contenu pour différencier 2 payloads du même générateur
        const marker = payload?.texte || payload?.contenu?.phrase
            || JSON.stringify(payload?.contenu?.pairs?.[0] || payload?.contenu?.items?.[0] || '');
        return `${genKey}::${String(marker).slice(0, 40)}`;
    }
    return payload.originalIndex != null ? payload.originalIndex : fallback;
};
